import React, { useEffect, useState, Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ToastContainer } from "react-toastify";

import LoggedInLayout from "../layout";
import { AuthProvider } from "../context/Auth/AuthContext";
import { TicketsContextProvider } from "../context/Tickets/TicketsContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import RouteWrapper from "./Route";
import BackdropLoading from "../components/BackdropLoading";

// Lazy-loaded pages
const Dashboard = lazy(() => import("../pages/Dashboard"));
const TicketResponsiveContainer = lazy(() => import("../pages/TicketResponsiveContainer"));
const Signup = lazy(() => import("../pages/Signup"));
const Login = lazy(() => import("../pages/Login"));
const Connections = lazy(() => import("../pages/Connections"));
const SettingsCustom = lazy(() => import("../pages/SettingsCustom"));
const Financeiro = lazy(() => import("../pages/Financeiro"));
const Users = lazy(() => import("../pages/Users"));
const Contacts = lazy(() => import("../pages/Contacts"));
const ContactImportPage = lazy(() => import("../pages/Contacts/import"));
const ChatMoments = lazy(() => import("../pages/Moments"));
const Queues = lazy(() => import("../pages/Queues"));
const Tags = lazy(() => import("../pages/Tags"));
const MessagesAPI = lazy(() => import("../pages/MessagesAPI"));
const Helps = lazy(() => import("../pages/Helps"));
const ContactLists = lazy(() => import("../pages/ContactLists"));
const ContactListItems = lazy(() => import("../pages/ContactListItems"));
const Companies = lazy(() => import("../pages/Companies"));
const QuickMessages = lazy(() => import("../pages/QuickMessages"));
const Schedules = lazy(() => import("../pages/Schedules"));
const Campaigns = lazy(() => import("../pages/Campaigns"));
const CampaignsConfig = lazy(() => import("../pages/CampaignsConfig"));
const CampaignReport = lazy(() => import("../pages/CampaignReport"));
const Annoucements = lazy(() => import("../pages/Annoucements"));
const Chat = lazy(() => import("../pages/Chat"));
const Prompts = lazy(() => import("../pages/Prompts"));
const AllConnections = lazy(() => import("../pages/AllConnections"));
const Reports = lazy(() => import("../pages/Reports"));
const FlowBuilderConfig = lazy(() => import("../pages/FlowBuilderConfig").then(m => ({ default: m.FlowBuilderConfig })));
const FlowBuilder = lazy(() => import("../pages/FlowBuilder"));
const FlowDefault = lazy(() => import("../pages/FlowDefault"));
const CampaignsPhrase = lazy(() => import("../pages/CampaignsPhrase"));
const Subscription = lazy(() => import("../pages/Subscription"));
const QueueIntegration = lazy(() => import("../pages/QueueIntegration"));
const Files = lazy(() => import("../pages/Files"));
const ToDoList = lazy(() => import("../pages/ToDoList"));
const Kanban = lazy(() => import("../pages/Kanban"));
const TagsKanban = lazy(() => import("../pages/TagsKanban"));
const ForgotPassword = lazy(() => import("../pages/ForgetPassWord"));
const ResetPassword = lazy(() => import("../pages/ResetPassword"));

const PageFallback = () => <BackdropLoading />;

const PrivateRoutes = () => {
  const [showCampaigns, setShowCampaigns] = useState(false);

  useEffect(() => {
    const cshow = localStorage.getItem("cshow");
    if (cshow !== undefined) {
      setShowCampaigns(true);
    }
  }, []);

  return (
    <LoggedInLayout>
      <Suspense fallback={<PageFallback />}>
        <Routes>
          <Route path="/" element={<RouteWrapper component={Dashboard} isPrivate />} />
          <Route path="/financeiro" element={<RouteWrapper component={Financeiro} isPrivate />} />
          <Route path="/companies" element={<RouteWrapper component={Companies} isPrivate />} />
          <Route path="/tickets/:ticketId?" element={<RouteWrapper component={TicketResponsiveContainer} isPrivate />} />
          <Route path="/connections" element={<RouteWrapper component={Connections} isPrivate />} />
          <Route path="/quick-messages" element={<RouteWrapper component={QuickMessages} isPrivate />} />
          <Route path="/todolist" element={<RouteWrapper component={ToDoList} isPrivate />} />
          <Route path="/schedules" element={<RouteWrapper component={Schedules} isPrivate />} />
          <Route path="/tags" element={<RouteWrapper component={Tags} isPrivate />} />
          <Route path="/contacts" element={<RouteWrapper component={Contacts} isPrivate />} />
          <Route path="/contacts/import" element={<RouteWrapper component={ContactImportPage} isPrivate />} />
          <Route path="/helps" element={<RouteWrapper component={Helps} isPrivate />} />
          <Route path="/users" element={<RouteWrapper component={Users} isPrivate />} />
          <Route path="/messages-api" element={<RouteWrapper component={MessagesAPI} isPrivate />} />
          <Route path="/settings" element={<RouteWrapper component={SettingsCustom} isPrivate />} />
          <Route path="/queues" element={<RouteWrapper component={Queues} isPrivate />} />
          <Route path="/reports" element={<RouteWrapper component={Reports} isPrivate />} />
          <Route path="/queue-integration" element={<RouteWrapper component={QueueIntegration} isPrivate />} />
          <Route path="/announcements" element={<RouteWrapper component={Annoucements} isPrivate />} />
          <Route path="/phrase-lists" element={<RouteWrapper component={CampaignsPhrase} isPrivate />} />
          <Route path="/flowbuilders" element={<RouteWrapper component={FlowBuilder} isPrivate />} />
          <Route path="/flowbuilder/:id?" element={<RouteWrapper component={FlowBuilderConfig} isPrivate />} />
          <Route path="/chats/:id?" element={<RouteWrapper component={Chat} isPrivate />} />
          <Route path="/files" element={<RouteWrapper component={Files} isPrivate />} />
          <Route path="/moments" element={<RouteWrapper component={ChatMoments} isPrivate />} />
          <Route path="/Kanban" element={<RouteWrapper component={Kanban} isPrivate />} />
          <Route path="/TagsKanban" element={<RouteWrapper component={TagsKanban} isPrivate />} />
          <Route path="/prompts" element={<RouteWrapper component={Prompts} isPrivate />} />
          <Route path="/allConnections" element={<RouteWrapper component={AllConnections} isPrivate />} />
          <Route path="/subscription" element={<RouteWrapper component={Subscription} isPrivate />} />
          {showCampaigns && (
            <>
              <Route path="/contact-lists" element={<RouteWrapper component={ContactLists} isPrivate />} />
              <Route path="/contact-lists/:contactListId/contacts" element={<RouteWrapper component={ContactListItems} isPrivate />} />
              <Route path="/campaigns" element={<RouteWrapper component={Campaigns} isPrivate />} />
              <Route path="/campaign/:campaignId/report" element={<RouteWrapper component={CampaignReport} isPrivate />} />
              <Route path="/campaigns-config" element={<RouteWrapper component={CampaignsConfig} isPrivate />} />
            </>
          )}
        </Routes>
      </Suspense>
    </LoggedInLayout>
  );
};

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TicketsContextProvider>
          <WhatsAppsProvider>
            <Suspense fallback={<PageFallback />}>
              <Routes>
                <Route path="/login" element={<RouteWrapper component={Login} />} />
                <Route path="/signup" element={<RouteWrapper component={Signup} />} />
                <Route path="/forgot-password" element={<RouteWrapper component={ForgotPassword} />} />
                <Route path="/reset-password" element={<RouteWrapper component={ResetPassword} />} />
                <Route path="/*" element={<PrivateRoutes />} />
              </Routes>
            </Suspense>
            <ToastContainer position="top-center" autoClose={3000} />
          </WhatsAppsProvider>
        </TicketsContextProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppRoutes;
